import { CopyOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons"
import {
  App,
  Avatar,
  Button,
  Checkbox,
  Empty,
  Flex,
  Modal,
  Select,
  Skeleton,
  Typography
} from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { notificationOptions } from "../../../constants/notification.config"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import {
  changeAttachmentAccessPermission,
  createAttachmentAccess,
  createAttachmentShareLink,
  disableAttachmentShareLink,
  getAttachmentAccess,
  getAttachmentShareLinks,
  removeAttachmentAccess
} from "../../Project/api/project-attachment-sharing.services.ts"
import { getProjectDocumentReviewers } from "../../Project/api/project-documents.services.ts"
import type {
  AttachmentPermission,
  ProjectAttachmentAccess,
  ProjectAttachmentShareLink,
  ShareLinkTarget
} from "../../Project/types/project-attachment-sharing.types.ts"
import { readServerMessage } from "../helpers/server-message.ts"
import { type FileItem } from "../types/file-item.types.ts"
import { TextInput } from "../../../components/Input/TextInput.tsx"

const { Text, Title } = Typography

interface ShareDialogProps {
  file: FileItem
  onClose: () => void
}

const PERMISSION_VALUES: AttachmentPermission[] = [
  "viewer",
  "contributor",
  "manager"
]

export function ShareDialog({ file, onClose }: ShareDialogProps) {
  const { t } = useTranslation()
  const { message, notification } = App.useApp()

  // An error stays on screen until the user dismisses it.
  const reportFailure = (failureKey: string, data?: unknown) =>
    notification.error({
      ...notificationOptions.error,
      message: t(failureKey),
      description: readServerMessage(data),
      duration: 0
    })

  const [access, setAccess] = useState<ProjectAttachmentAccess[] | null>(null)
  const [links, setLinks] = useState<ProjectAttachmentShareLink[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [people, setPeople] = useState<{ label: string; value: string }[]>([])

  const [addingUserId, setAddingUserId] = useState<string | null>(null)
  const [addingPermission, setAddingPermission] =
    useState<AttachmentPermission>("viewer")
  const [adding, setAdding] = useState(false)
  const [busyAccessId, setBusyAccessId] = useState<string | null>(null)

  const [allowDownload, setAllowDownload] = useState(true)
  const [creatingLink, setCreatingLink] = useState(false)
  const [disablingLinkId, setDisablingLinkId] = useState<string | null>(null)

  const permissionOptions = PERMISSION_VALUES.map((value) => ({
    value,
    label: t(`files.sharing.permission.${value}`)
  }))

  // Access grants and reviewers exist for documents only.
  const isDocument = file.source === "document"
  const linkTarget = useMemo<ShareLinkTarget>(
    () =>
      isDocument ? { attachment: file.id } : { album_attachment: file.id },
    [file.id, isDocument]
  )

  const load = useCallback(async () => {
    setAccess(null)
    setLinks(null)
    setLoadFailed(false)
    const [accessResponse, linksResponse, reviewersResponse] =
      await Promise.all([
        isDocument ? getAttachmentAccess(file.id) : undefined,
        getAttachmentShareLinks(linkTarget),
        isDocument ? getProjectDocumentReviewers(file.id) : undefined
      ])
    if (
      !linksResponse.ok ||
      !linksResponse.data ||
      (accessResponse !== undefined &&
        (!accessResponse.ok || !accessResponse.data))
    ) {
      setLoadFailed(true)
      setAccess([])
      setLinks([])
      return
    }
    setAccess(
      accessResponse?.ok && accessResponse.data
        ? accessResponse.data.results
        : []
    )
    // A backend that predates `album_attachment` ignores the filter and answers with every link.
    setLinks(
      linksResponse.data.results.filter((link) =>
        isDocument
          ? link.attachment === file.id
          : link.album_attachment === file.id
      )
    )
    if (reviewersResponse?.ok && reviewersResponse.data) {
      setPeople(reviewersResponse.data)
    }
  }, [file.id, isDocument, linkTarget])

  useEffect(() => {
    void load()
  }, [load])

  const grantedUserIds = new Set((access ?? []).map((entry) => entry.user?.id))
  const addablePeople = people.filter(
    (person) => !grantedUserIds.has(person.value)
  )
  const activeLink = (links ?? []).find((link) => link.is_active) ?? null

  const handleAdd = async () => {
    if (!addingUserId) return
    setAdding(true)
    const response = await createAttachmentAccess({
      attachment: file.id,
      user_id: addingUserId,
      permission: addingPermission
    })
    setAdding(false)
    if (!response.ok || !response.data) {
      reportFailure("files.sharing.addFailed", response.data)
      return
    }
    message.success(t("files.sharing.addSucceeded"))
    setAddingUserId(null)
    setAddingPermission("viewer")
    await load()
  }

  const handleChangePermission = async (
    entry: ProjectAttachmentAccess,
    permission: AttachmentPermission
  ) => {
    setBusyAccessId(entry.id)
    const response = await changeAttachmentAccessPermission(
      entry.id,
      permission
    )
    setBusyAccessId(null)
    if (!response.ok) {
      reportFailure("files.sharing.permissionFailed", response.data)
      return
    }
    setAccess((prev) =>
      (prev ?? []).map((item) =>
        item.id === entry.id ? { ...item, permission } : item
      )
    )
  }

  const handleRemove = async (entry: ProjectAttachmentAccess) => {
    setBusyAccessId(entry.id)
    const response = await removeAttachmentAccess(entry.id)
    setBusyAccessId(null)
    if (!response.ok) {
      reportFailure("files.sharing.removeFailed", response.data)
      return
    }
    message.success(t("files.sharing.removeSucceeded"))
    setAccess((prev) => (prev ?? []).filter((item) => item.id !== entry.id))
  }

  const handleCreateLink = async () => {
    setCreatingLink(true)
    const response = await createAttachmentShareLink({
      ...linkTarget,
      allow_download: allowDownload
    })
    setCreatingLink(false)
    if (!response.ok || !response.data) {
      reportFailure("files.sharing.linkCreateFailed", response.data)
      return
    }
    const created = response.data.details.data
    setLinks((prev) => [created, ...(prev ?? [])])
  }

  const handleDisableLink = async (link: ProjectAttachmentShareLink) => {
    setDisablingLinkId(link.id)
    const response = await disableAttachmentShareLink(link.id)
    setDisablingLinkId(null)
    if (!response.ok) {
      reportFailure("files.sharing.linkDisableFailed", response.data)
      return
    }
    setLinks((prev) =>
      (prev ?? []).map((item) =>
        item.id === link.id
          ? { ...item, is_active: false, disabled_at: new Date().toISOString() }
          : item
      )
    )
  }

  const handleCopyLink = async (link: ProjectAttachmentShareLink) => {
    try {
      await navigator.clipboard.writeText(link.url)
      message.success(t("files.sharing.linkCopied"))
    } catch {
      reportFailure("files.sharing.linkCopyFailed")
    }
  }

  const loading = access === null || links === null

  return (
    <Modal
      open
      title={t("files.sharing.title", { name: file.name })}
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {t("files.sharing.done")}
        </Button>
      }
      width={520}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : loadFailed ? (
        <Empty description={t("files.sharing.loadFailed")}>
          <Button size="small" onClick={() => void load()}>
            {t("files.activity.retry")}
          </Button>
        </Empty>
      ) : (
        <Flex vertical gap="middle">
          {isDocument && (
            <>
              <Flex vertical gap={8}>
                <Title level={5} className="!mb-0">
                  {t("files.sharing.peopleWithAccess")}
                </Title>
                {access.length === 0 ? (
                  <Text type="secondary">{t("files.sharing.noPeople")}</Text>
                ) : (
                  <Flex vertical gap={4}>
                    {access.map((entry) => (
                      <Flex
                        key={entry.id}
                        align="center"
                        justify="space-between"
                        gap={8}
                      >
                        <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                          <Avatar size="small" src={entry.user?.avatar}>
                            {getEntityName(entry.user, "?").charAt(0)}
                          </Avatar>
                          <Text
                            ellipsis={{
                              tooltip: getEntityName(
                                entry.user,
                                entry.user?.email
                              )
                            }}
                          >
                            {getEntityName(entry.user, entry.user?.email)}
                          </Text>
                        </Flex>
                        <Flex align="center" gap={4}>
                          <Select<AttachmentPermission>
                            size="small"
                            value={entry.permission}
                            options={permissionOptions}
                            disabled={busyAccessId === entry.id}
                            onChange={(value) =>
                              void handleChangePermission(entry, value)
                            }
                            style={{ width: 120 }}
                          />
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            disabled={busyAccessId === entry.id}
                            aria-label={t("files.sharing.removeAccess")}
                            onClick={() => void handleRemove(entry)}
                          />
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Flex>

              <Flex vertical gap={8}>
                <Text type="secondary">{t("files.sharing.addPeople")}</Text>
                <Flex gap={8}>
                  <Select<string>
                    showSearch
                    allowClear
                    placeholder={t("files.sharing.addPeoplePlaceholder")}
                    value={addingUserId ?? undefined}
                    onChange={(value) => setAddingUserId(value ?? null)}
                    options={addablePeople}
                    optionFilterProp="label"
                    style={{ flex: 1 }}
                  />
                  <Select<AttachmentPermission>
                    value={addingPermission}
                    options={permissionOptions}
                    onChange={setAddingPermission}
                    style={{ width: 120 }}
                  />
                  <Button
                    type="primary"
                    disabled={!addingUserId}
                    loading={adding}
                    onClick={() => void handleAdd()}
                  >
                    {t("files.sharing.add")}
                  </Button>
                </Flex>
              </Flex>
            </>
          )}

          <Flex vertical gap={8}>
            <Title level={5} className="!mb-0">
              {t("files.sharing.shareLink")}
            </Title>
            {activeLink ? (
              <Flex vertical gap={8}>
                <Flex gap={8}>
                  <TextInput
                    disableAutoCapitalize
                    readOnly
                    value={activeLink.url}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => void handleCopyLink(activeLink)}
                  >
                    {t("files.sharing.copyLink")}
                  </Button>
                </Flex>
                <Flex align="center" justify="space-between">
                  <Text type="secondary" className="text-xs">
                    {activeLink.allow_download
                      ? t("files.sharing.downloadAllowed")
                      : t("files.sharing.downloadDisabled")}
                  </Text>
                  <Button
                    danger
                    size="small"
                    loading={disablingLinkId === activeLink.id}
                    onClick={() => void handleDisableLink(activeLink)}
                  >
                    {t("files.sharing.disableLink")}
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Flex vertical gap={8} align="start">
                <Checkbox
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                >
                  {t("files.sharing.allowDownload")}
                </Checkbox>
                <Button
                  icon={<LinkOutlined />}
                  loading={creatingLink}
                  onClick={() => void handleCreateLink()}
                >
                  {t("files.sharing.createLink")}
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}
